
import React, { useRef, useState } from 'react';
import { 
  Building2, MapPin, Hash, Camera, Save, Info, FileText, 
  Settings, Clock, Tag, UserCheck, ShieldCheck, CheckCircle2,
  Trash2, Plus, Globe, Mail, Phone
} from 'lucide-react';
import { Card, Badge } from './SharedComponents';
import { Company } from './types';

interface SettingsUnitProps {
  company?: Company;
  onUpdate: (c: Partial<Company>) => void;
}

export const SettingsUnit = ({ company, onUpdate }: SettingsUnitProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: company?.name || '',
    cnpj: company?.cnpj || '',
    address: company?.address || '',
    neighborhood: company?.neighborhood || '',
    city: company?.city || '',
    state: company?.state || '',
    whatsapp: company?.whatsapp || '',
    contactEmail: company?.contactEmail || '',
    logo: company?.logo || '',
    policy: company?.policy || `Para garantir a pontualidade da operação, é obrigatório que haja um responsável no local no dia e horário agendados para entrega/montagem/retirada. Caso não haja responsável e a equipe seja impedida de executar o serviço no horário combinado, poderá ser cobrada taxa de deslocamento adicional.`,
  });

  React.useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        cnpj: company.cnpj || '',
        address: company.address || '',
        neighborhood: company.neighborhood || '',
        city: company.city || '',
        state: company.state || '',
        whatsapp: company.whatsapp || '',
        contactEmail: company.contactEmail || '',
        logo: company.logo || '',
        policy: company.policy || formData.policy,
      });
    }
  }, [company, formData.policy]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setFormData(prev => ({ ...prev, logo: base64 }));
        onUpdate({ logo: base64 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate(formData);
    alert("Dados da unidade atualizados com sucesso!");
  };

  return (
    <div className="space-y-10 animate-in slide-in-from-bottom-2 duration-500 pb-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Coluna Esquerda: Formulário Detalhado */}
        <div className="lg:col-span-8 space-y-6">
           <Card className="p-10 bg-white border border-gray-100 rounded-[48px] shadow-2xl">
              <form onSubmit={handleSave} className="space-y-10">
                 <section className="space-y-8">
                    <h3 className="text-2xl font-black uppercase tracking-tight text-secondary flex items-center gap-4 italic">
                       <div className="p-3 bg-primary/10 rounded-2xl text-primary"><Building2 size={24} /></div> Informações Jurídicas & Contato
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                       <InputGroup label="Razão Social / Nome Fantasia" value={formData.name} onChange={(v:any) => setFormData({...formData, name: v})} />
                       <InputGroup label="CNPJ Principal" value={formData.cnpj} onChange={(v:any) => setFormData({...formData, cnpj: v})} />
                       <InputGroup label="WhatsApp Central" value={formData.whatsapp} onChange={(v:any) => setFormData({...formData, whatsapp: v})} icon={Phone} />
                       <InputGroup label="E-mail Administrativo" value={formData.contactEmail} onChange={(v:any) => setFormData({...formData, contactEmail: v})} icon={Mail} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                       <div className="md:col-span-2">
                          <InputGroup label="Endereço (Rua/Número)" value={formData.address} onChange={(v:any) => setFormData({...formData, address: v})} />
                       </div>
                       <InputGroup label="Bairro" value={formData.neighborhood} onChange={(v:any) => setFormData({...formData, neighborhood: v})} />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                       <div className="md:col-span-3">
                          <InputGroup label="Cidade" value={formData.city} onChange={(v:any) => setFormData({...formData, city: v})} />
                       </div>
                       <InputGroup label="Estado (UF)" value={formData.state} onChange={(v:any) => setFormData({...formData, state: v.toUpperCase()})} maxLength={2} />
                    </div>
                 </section>

                 <section className="space-y-6 pt-10 border-t border-gray-50">
                    <h3 className="text-xl font-black uppercase tracking-tight text-secondary flex items-center gap-4 italic">
                       <div className="p-3 bg-blue-50 rounded-2xl text-blue-600"><FileText size={24} /></div> Normas e Cláusulas do Romaneio
                    </h3>
                    <div className="space-y-4">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Texto Jurídico da O.S. (Base de Contrato)</label>
                       <textarea 
                        className="w-full bg-slate-50 border border-slate-100 p-8 rounded-[40px] font-bold text-secondary text-sm focus:ring-8 focus:ring-primary/5 transition-all outline-none resize-none shadow-inner min-h-[220px] leading-relaxed"
                        value={formData.policy}
                        onChange={e => setFormData({...formData, policy: e.target.value})}
                       />
                       <div className="flex items-center gap-3 text-primary bg-primary/5 p-4 rounded-2xl border border-primary/10">
                          <Info size={16} />
                          <span className="text-[9px] font-black uppercase tracking-widest leading-relaxed">As alterações neste texto serão aplicadas instantaneamente em todos os novos PDFs gerados.</span>
                       </div>
                    </div>
                 </section>

                 <div className="pt-6 flex justify-end">
                    <button type="submit" className="bg-secondary text-white px-14 py-5 rounded-[28px] font-black uppercase text-xs tracking-[0.2em] flex items-center gap-3 shadow-2xl shadow-secondary/20 hover:bg-black transition-all active:scale-95">
                       <Save size={20} /> Efetivar Atualizações
                    </button>
                 </div>
              </form>
           </Card>
        </div>

        {/* Coluna Direita: Logo & Status */}
        <div className="lg:col-span-4 space-y-6">
           <Card className="p-10 bg-white border border-gray-100 rounded-[48px] shadow-2xl text-center flex flex-col items-center">
              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-10">Identidade Visual (Core)</h4>
              <div className="relative group mb-8">
                 <div className="w-48 h-48 rounded-[56px] bg-slate-50 border-4 border-white shadow-2xl flex items-center justify-center overflow-hidden transition-all group-hover:scale-105">
                    {formData.logo ? <img src={formData.logo} className="w-full h-full object-contain p-6" /> : <Building2 size={64} className="text-slate-200" />}
                 </div>
                 <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute inset-0 flex flex-col items-center justify-center text-white bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity z-10 rounded-[56px]"
                 >
                    <Camera size={36} />
                    <span className="text-[9px] font-black uppercase tracking-widest mt-3">Alterar Logo</span>
                 </button>
                 <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleLogoChange} />
              </div>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest leading-relaxed max-w-[200px]">Este logo será impresso no cabeçalho dos romaneios e faturas.</p>
           </Card>

           <Card className="p-10 bg-[#0f172a] text-white rounded-[48px] shadow-2xl space-y-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-5 rotate-12"><ShieldCheck size={120} /></div>
              <div className="flex items-center gap-4 relative z-10">
                 <ShieldCheck size={28} className="text-primary" />
                 <h4 className="text-lg font-black uppercase italic tracking-tight">Compliance SaaS</h4>
              </div>
              <div className="space-y-5 relative z-10">
                 <div className="p-5 bg-white/5 border border-white/10 rounded-3xl">
                    <p className="text-[9px] font-black text-primary uppercase tracking-[0.2em] mb-2">Renovação da Licença</p>
                    <p className="text-xl font-black">{company?.licenseExpiry || '31/12/2025'}</p>
                 </div>
                 <div className="p-5 bg-white/5 border border-white/10 rounded-3xl flex justify-between items-center">
                    <div>
                       <p className="text-[9px] font-black text-primary uppercase tracking-[0.2em] mb-1">Assinatura</p>
                       <p className="text-lg font-black">{company?.plan || 'PRO'}</p>
                    </div>
                    <Badge className="bg-primary text-white border-none px-4 py-1.5 font-black text-[9px]">ATIVO</Badge>
                 </div>
              </div>
           </Card>
        </div>

      </div>
    </div>
  );
};

const InputGroup = ({ label, value, onChange, placeholder, type = "text", maxLength, icon: Icon }: any) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">{label}</label>
    <div className="relative group">
       {Icon && <Icon size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-primary opacity-40 group-focus-within:opacity-100 transition-opacity" />}
       <input 
        type={type} 
        maxLength={maxLength} 
        className={`w-full bg-slate-50 border border-slate-100 p-5 rounded-[24px] outline-none font-bold text-secondary focus:bg-white focus:ring-8 focus:ring-primary/5 transition-all text-sm shadow-inner ${Icon ? 'pl-14' : ''}`} 
        value={value} 
        onChange={e => onChange(e.target.value)} 
        placeholder={placeholder}
      />
    </div>
  </div>
);
